import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLangMap } from '../../hooks/useLanguages';
import { useAuth } from '../../context/AuthContext';
import { LearningProgressService } from '../../services/LearningProgressService';
import { AchievementsService } from '../../services/AchievementsService';
import { SrsService } from '../../services/SrsService';
import { fetchData } from '../../services/dataApi';
import { useAppConfig } from '../../hooks/useAppConfig';

const SPEEDS = [
  { label: '0.5×', value: 0.5 },
  { label: '0.75×', value: 0.75 },
  { label: '1×', value: 1.0 },
];

function getSlideEntry(index, n) {
  if (!index) return { text: '' };
  const key = Object.keys(index).sort((a, b) => Number(a) - Number(b))[n - 1];
  return key ? { ...index[key], audioId: key } : { text: '' };
}

export default function AudioSlideshowPage() {
  const { t } = useTranslation();
  const { langCode } = useParams();
  const navigate = useNavigate();
  const { langMap } = useLangMap();
  const { languagePrefs } = useAuth();
  const config = useAppConfig(languagePrefs?.nativeLanguage);
  const meta = langMap[langCode] ?? { label: langCode.toUpperCase() };

  const [index, setIndex] = useState(null);
  const [current, setCurrent] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const [speed, setSpeed] = useState(0.75);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loop, setLoop] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [dictation, setDictation] = useState('');
  const [dictationResult, setDictationResult] = useState(null);
  const langPair = languagePrefs
    ? `${languagePrefs.nativeLanguage}_${languagePrefs.targetLanguage}`
    : `en_${langCode}`;

  const audioRef   = useRef(null);
  const utteranceRef = useRef(null);
  const autoPlayRef = useRef(autoPlay);
  const currentRef  = useRef(current);
  const speedRef    = useRef(speed);
  const totalRef    = useRef(0);
  const playFnRef   = useRef(null);
  const loopRef = useRef(loop);
  const loopPlaysRef = useRef(0);

  useEffect(() => { autoPlayRef.current = autoPlay; }, [autoPlay]);
  useEffect(() => { currentRef.current = current; }, [current]);
  useEffect(() => { speedRef.current = speed; }, [speed]);
  useEffect(() => { loopRef.current = loop; loopPlaysRef.current = 0; }, [loop]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setCurrent(1);

    async function load() {
      let data = null;
      if (config.audioLangs.includes(langCode)) {
        try {
          const response = await fetch(`${process.env.PUBLIC_URL}/audio/${langCode}/index.json`);
          if (response.ok) data = await response.json();
        } catch {
          data = null;
        }
      }
      if (!data || Object.keys(data).length === 0) {
        try {
          const packs = await Promise.all(
            [1, 2, 3, 4].map((id) => fetchData(`speaking-listening/${langCode}/${id}.json`)),
          );
          const items = packs.flatMap((pack) => pack.items || []).filter((item) => item?.text);
          data = Object.fromEntries(
            items.map((item, index) => [String(index + 1), { text: item.text, synthetic: true }]),
          );
        } catch {
          data = null;
        }
      }
      if (!active) return;
      if (!data || Object.keys(data).length === 0) {
        setError(true);
      } else {
        setIndex(data);
        totalRef.current = Object.keys(data).length;
      }
      setLoading(false);
    }

    load();
    return () => {
      active = false;
      stopAudio();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langCode, config.audioLangs]);

  function stopAudio() {
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (utteranceRef.current && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      utteranceRef.current = null;
    }
    setPlaying(false);
  }

  function playSlide(n) {
    stopAudio();
    const entry = getSlideEntry(index, n);
    if (!entry.text) return;
    const onEnded = () => {
      setPlaying(false);
      const snapshot = LearningProgressService.record(langPair, 'listen', 1, {
        activityId: `clip:${langCode}:${entry.audioId || n}`,
      });
      if (snapshot.listeningStreak >= 3) {
        AchievementsService.tryUnlock('listening_streak').then(AchievementsService.showBadgeToast);
      }
      if (loopRef.current && loopPlaysRef.current < 2) {
        loopPlaysRef.current += 1;
        setTimeout(() => playFnRef.current?.(currentRef.current), 350);
        return;
      }
      loopPlaysRef.current = 0;
      if (autoPlayRef.current && currentRef.current < totalRef.current) {
        setTimeout(() => playFnRef.current?.(currentRef.current + 1), 700);
      }
    };

    setCurrent(n);
    currentRef.current = n;
    setPlaying(true);
    if (entry.synthetic) {
      if (!window.speechSynthesis) {
        setPlaying(false);
        return;
      }
      const utterance = new SpeechSynthesisUtterance(entry.text);
      utterance.lang = meta.tts || langCode;
      utterance.rate = Math.max(0.5, speedRef.current);
      utterance.onend = onEnded;
      utterance.onerror = () => setPlaying(false);
      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
      return;
    }

    const audio = new Audio(`${process.env.PUBLIC_URL}/audio/${langCode}/${entry.audioId}.mp3`);
    audioRef.current = audio;
    audio.playbackRate = speedRef.current;
    audio.play().catch(() => setPlaying(false));
    audio.onended = onEnded;
  }
  playFnRef.current = playSlide;

  function togglePlay() {
    if (playing) {
      stopAudio();
    } else {
      playSlide(current);
    }
  }

  function goTo(n) {
    stopAudio();
    setCurrent(n);
    setShowTranscript(false);
    setDictation('');
    setDictationResult(null);
  }

  function checkDictation() {
    const normalize = (value) => value.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, '').replace(/\s+/g, ' ').trim();
    const correct = normalize(dictation) === normalize(text);
    setDictationResult(correct);
    if (correct) {
      const entry = getSlideEntry(index, current);
      LearningProgressService.record(langPair, 'listen', 1, {
        activityId: `clip:${langCode}:${entry.audioId || current}`,
      });
    } else if (dictation.trim()) {
      SrsService.review({
        wordKey: `listen_${langCode}_${current}`,
        nativeWord: 'Listening dictation',
        targetWord: text,
        category: 'listening',
        langPair,
        easiness: 2.5,
        interval: 1,
        repetitions: 0,
        nextReview: new Date().toISOString(),
      }, 2);
    }
  }

  function prev() {
    const n = Math.max(1, current - 1);
    playing ? playSlide(n) : goTo(n);
  }

  function next() {
    const n = Math.min(totalRef.current, current + 1);
    playing ? playSlide(n) : goTo(n);
  }

  const keys  = index ? Object.keys(index).sort((a, b) => Number(a) - Number(b)) : [];
  const total  = keys.length;
  const text   = getSlideEntry(index, current).text ?? '';
  const pct    = total > 1 ? ((current - 1) / (total - 1)) * 100 : total === 1 ? 100 : 0;

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-4xl">📭</p>
        <p className="font-bold text-slate-700">{t('audio.noAudio')} "{langCode}"</p>
        <button
          onClick={() => navigate(-1)}
          className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50"
        >
          {t('audio.goBack')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at 20% 0%, #e0e7ff 0%, #f8fafc 50%, #fdf4ff 100%)' }}>
      <div className="mx-auto max-w-lg px-4 py-10 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-slate-400">{t('audio.title')}</p>
            <h1 className="text-2xl font-black text-slate-900">
              {meta.label}
            </h1>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50 transition"
          >
            {t('audio.back')}
          </button>
        </div>

        {/* Progress bar */}
        <div>
          <div className="mb-1 flex justify-between text-xs font-semibold text-slate-400">
            <span>{t('audio.recordingOf', { current, total })}</span>
            <span>{Math.round(pct)}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Script card */}
        <div
          className="relative flex min-h-[260px] items-center justify-center rounded-3xl p-8 shadow-2xl shadow-indigo-400/20"
          style={{
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%)',
          }}
        >
          <span className="absolute top-4 left-4 rounded-full bg-white/20 px-3 py-1 text-xs font-black text-white/80">
            {current}
          </span>

          {playing && (
            <span className="absolute top-4 right-4 flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-bold text-white/90">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
              </span>
              {t('audio.playing')}
            </span>
          )}

          {showTranscript ? (
            <p dir={meta.dir || 'ltr'} className="text-center font-black leading-tight text-white" style={{ fontSize: text.length > 10 ? '2rem' : '4rem' }}>
              {text || '—'}
            </p>
          ) : (
            <div className="text-center text-white"><p className="text-5xl">🎧</p><p className="mt-3 text-sm font-bold text-white/80">{t('audio.listenFirst')}</p></div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={prev}
            disabled={current === 1}
            className="flex-1 rounded-2xl border border-slate-200 bg-white py-3.5 text-sm font-bold text-slate-600 shadow-sm disabled:opacity-25 hover:bg-slate-50 transition"
          >
            ← {t('audio.prev')}
          </button>

          <button
            onClick={togglePlay}
            className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-xl text-white shadow-lg shadow-indigo-400/30 transition hover:scale-105"
          >
            {playing ? '⏸' : '▶'}
          </button>

          <button
            onClick={next}
            disabled={current === total}
            className="flex-1 rounded-2xl border border-slate-200 bg-white py-3.5 text-sm font-bold text-slate-600 shadow-sm disabled:opacity-25 hover:bg-slate-50 transition"
          >
            {t('audio.nextArrow')}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => setShowTranscript((value) => !value)} className="rounded-xl border border-indigo-200 bg-white px-3 py-2.5 text-xs font-bold text-indigo-700 shadow-sm">
            {showTranscript ? t('audio.hideTranscript') : t('audio.revealTranscript')}
          </button>
          <button onClick={() => setLoop((value) => !value)} className={`rounded-xl border px-3 py-2.5 text-xs font-bold shadow-sm ${loop ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 bg-white text-slate-600'}`}>
            🔁 {loop ? t('audio.replayLoopOn') : t('audio.replayLoopOff')}
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-black text-slate-800">{t('audio.typeWhatYouHeard')}</p>
          <p className="mt-0.5 text-xs text-slate-500">{t('audio.dictationDesc')}</p>
          <div className="mt-3 flex gap-2">
            <input value={dictation} onChange={(event) => { setDictation(event.target.value); setDictationResult(null); }} onKeyDown={(event) => event.key === 'Enter' && checkDictation()} dir={meta.dir || 'ltr'} className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" placeholder={t('audio.dictationPlaceholder')} />
            <button onClick={checkDictation} disabled={!dictation.trim()} className="rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-40">{t('audio.check')}</button>
          </div>
          {dictationResult !== null && <p className={`mt-2 text-xs font-bold ${dictationResult ? 'text-emerald-600' : 'text-amber-600'}`}>{dictationResult ? t('audio.dictationCorrect') : t('audio.dictationIncorrect')}</p>}
        </div>

        {/* Options row */}
        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-3.5 shadow-sm">
          <button
            onClick={() => setAutoPlay((v) => !v)}
            className="flex items-center gap-2.5"
          >
            <span
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${autoPlay ? 'bg-indigo-600' : 'bg-slate-300'}`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${autoPlay ? 'translate-x-4' : 'translate-x-1'}`}
              />
            </span>
            <span className="text-sm font-semibold text-slate-700">{t('audio.autoPlay')}</span>
          </button>

          <div className="flex items-center gap-1">
            <span className="mr-1 text-xs font-semibold text-slate-400">{t('audio.speed')}</span>
            {SPEEDS.map((s) => (
              <button
                key={s.value}
                onClick={() => {
                  setSpeed(s.value);
                  speedRef.current = s.value;
                  if (audioRef.current) audioRef.current.playbackRate = s.value;
                }}
                className={`rounded-lg px-2.5 py-1 text-xs font-bold transition ${
                  speed === s.value
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Recording scrubber */}
        {total > 1 && (
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-black uppercase tracking-widest text-slate-400">{t('audio.jumpTo')}</p>
              <span className="rounded-lg bg-indigo-50 px-2.5 py-1 text-xs font-black text-indigo-600">
                {current} / {total}
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={total}
              value={current}
              onChange={(e) => {
                const n = Number(e.target.value);
                playing ? playSlide(n) : goTo(n);
              }}
              className="w-full cursor-pointer accent-indigo-600"
            />
            <div className="mt-1.5 flex justify-between text-[10px] font-semibold text-slate-400">
              <span>1</span>
              <span>{total}</span>
            </div>
          </div>
        )}

        {/* Attribution */}
        <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/70 px-5 py-4 shadow-sm backdrop-blur-sm">
          <span className="mt-0.5 text-lg leading-none">🎙️</span>
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-0.5">{t('audio.audioSource')}</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              Real speaker recordings sourced from{' '}
              <a
                href="https://commons.wikimedia.org"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-indigo-600 hover:text-indigo-800 hover:underline transition-colors"
              >
                Wikimedia Commons
              </a>
              {' '}CC0 pronunciation files, with older local tracks from LinguaLibre.org where available. CC0 files are released under{' '}
              <a
                href="https://creativecommons.org/publicdomain/zero/1.0/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-slate-500 hover:text-slate-700 hover:underline transition-colors"
              >
                CC0 1.0
              </a>
              .
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
